import React, { useCallback, useEffect, useState } from "react";
import {
  View,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
} from "react-native";
import Text from "@components/CustomText";
import colors from "@constants/color";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import BrokenCall from "@assets/svg/BrokenCall";
import { getBrokenCalls } from "@services/api";

const PAGE_LIMIT = 20;

function formatTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;
  const timeStr = `${hour12}:${minutes} ${ampm}`;

  if (isToday) return `Today, ${timeStr}`;
  if (isYesterday) return `Yesterday, ${timeStr}`;

  const day = date.getDate();
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${day} ${months[date.getMonth()]}, ${timeStr}`;
}

function BrokenCallReport({ navigation }) {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchBrokenCalls = useCallback(
    async (pageNum = 1, append = false) => {
      try {
        if (pageNum === 1) setIsLoading(true);
        else setIsLoadingMore(true);

        const response = await getBrokenCalls(pageNum, PAGE_LIMIT);
        const result = response.data;

        setData((prev) =>
          append ? [...prev, ...result.data] : result.data,
        );
        setHasNext(result.pagination.hasNext);
        setTotal(result.pagination.total);
        setPage(pageNum);
      } catch (error) {
        console.log("BrokenCallReport fetch error:", error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchBrokenCalls(1);
  }, [fetchBrokenCalls]);

  const loadMore = () => {
    if (!hasNext || isLoadingMore) return;
    fetchBrokenCalls(page + 1, true);
  };

  const createCall = useCallback((phone) => {
    if (!phone) return;
    Linking.openURL(`tel:${phone}`);
  }, []);

  const renderItem = useCallback(
    ({ item }) => (
      <View style={styles.card}>
        <View style={styles.cardLeft}>
          <Text variant="title_16" style={styles.name}>
            {item.cx_name}
          </Text>
          <Text variant="body_text_3" style={styles.phone}>
            +91 {item.user_phone}
          </Text>
          <View style={styles.metaRow}>
            <View style={styles.attemptBadge}>
              <Text variant="caption_1" style={styles.attemptText}>
                {item.total_attempts} {item.total_attempts === 1 ? "attempt" : "attempts"}
              </Text>
            </View>
            <Text variant="caption_1" style={styles.timeText}>
              {formatTime(item.last_call_triggered_at)}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          style={styles.callButton}
          activeOpacity={0.7}
          onPress={() => createCall(item.user_phone)}
        >
          <BrokenCall width={18} height={18} color={colors.white} />
          <Text variant="body_text_3" style={styles.callButtonText}>
            Retry
          </Text>
        </TouchableOpacity>
      </View>
    ),
    [createCall],
  );

  const keyExtractor = useCallback(
    (item, index) => `${item.user_phone}-${index}`,
    [],
  );

  const renderFooter = () => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.footerLoader}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  };

  const renderEmpty = () => {
    if (isLoading) return null;
    return (
      <View style={styles.emptyContainer}>
        <BrokenCall width={48} height={48} color={colors.lightMediumBlack} />
        <Text variant="title_16" style={styles.emptyTitle}>
          No broken calls left.
        </Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle headertitle="Broken Calls" navigation={navigation} />
      {!isLoading && data.length > 0 && (
        <View style={styles.summaryBar}>
          <Text variant="body_text_3" style={styles.summaryText}>
            {total} {total === 1 ? "customer" : "customers"} with broken calls
          </Text>
        </View>
      )}
      {isLoading ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={data}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          contentContainerStyle={styles.listContent}
          onEndReached={loadMore}
          onEndReachedThreshold={0.4}
          ListFooterComponent={renderFooter}
          ListEmptyComponent={renderEmpty}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.primaryBg,
  },
  summaryBar: {
    backgroundColor: colors.white,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.mercury,
  },
  summaryText: {
    color: colors.mediumBlacklightBlack,
  },
  listContent: {
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 24,
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginTop: 8,
    flexDirection: "row",
    alignItems: "center",
    elevation: 2,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  cardLeft: {
    flex: 1,
    marginRight: 12,
  },
  name: {
    color: colors.black,
    marginBottom: 2,
  },
  phone: {
    color: colors.mediumBlacklightBlack,
    marginBottom: 8,
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  attemptBadge: {
    backgroundColor: colors.lightRed,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginRight: 8,
  },
  attemptText: {
    color: colors.plain_red,
    fontSize: 11,
  },
  timeText: {
    color: colors.lightMediumBlack,
    fontSize: 11,
  },
  callButton: {
    backgroundColor: colors.plain_green,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  callButtonText: {
    color: colors.white,
    marginLeft: 6,
    fontWeight: "600",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  footerLoader: {
    paddingVertical: 16,
    alignItems: "center",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 120,
  },
  emptyTitle: {
    color: colors.black,
    marginTop: 16,
  },
  emptySubtitle: {
    color: colors.lightMediumBlack,
    marginTop: 4,
  },
});

export default BrokenCallReport;
